import { TokenKind } from '../../json/tokens';
import { scanStructure, structuralTokens, textOf } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/** The three literals a pasted Python `dict` brings with it. */
const PYTHON: Readonly<Record<string, string>> = {
  True: 'true',
  False: 'false',
  None: 'null',
};

/**
 * `True` → `true`, `False` → `false`, `None` → `null`.
 *
 * A pasted `repr()` of a Python dict is one of the most common broken inputs a JSON tool
 * sees, and the mapping is exact rather than interpretive — which is why this is `safe`.
 * The legacy pipeline had no rule for it; its unquoted-value regex reached `True` first and
 * produced `{"a":"True"}`, turning a boolean into a string (recon §3.3, B21). A type that
 * changes silently is precisely the failure ADR-0012 exists to prevent.
 */
export const normalizePythonLiteralRule: RepairRule = {
  id: 'normalize-python-literal',
  confidence: 'safe',
  priority: 35,
  resolves: ['literal/python-keyword'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const { namedKeys } = scanStructure(ctx.source, ctx.tokens);
    const proposals: RepairProposal[] = [];

    for (const token of structuralTokens(ctx.source, ctx.tokens)) {
      if (token.kind !== TokenKind.Unknown) continue;
      // Only a token the grammar reads as a key AND that is followed by a colon is
      // provably a property name; that is the one quote-unquoted-key claims. Anything
      // looser would skip the `Infinity` of `-Infinity`, which recovery also parks in key
      // position.
      if (namedKeys.has(token)) continue;

      const literal = textOf(ctx.source, token);
      const replacement = PYTHON[literal];
      if (replacement === undefined) continue;

      proposals.push({
        ruleId: 'normalize-python-literal',
        span: { from: token.from, to: token.to },
        edits: [{ from: token.from, to: token.to, insert: replacement }],
        labelCode: 'fix.normalize-python-literal',
        labelParams: { literal, replacement },
        resolves: 'literal/python-keyword',
      });
    }

    return proposals;
  },
};
